import React, { useState, useCallback, useEffect } from 'react';
import {
  BEYOND_BIRD_MEMORY_LOGICAL_WIDTH,
  BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT,
} from './beyondBirdMemoryLayout';
import './BeyondBirdTreeSlotEditor.css';

const MIN_HIT_RADIUS = 24;
const MAX_HIT_RADIUS = 160;

/**
 * @param {HTMLElement} el
 * @param {number} clientX
 * @param {number} clientY
 */
function clientToLogical(el, clientX, clientY) {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    throw new Error('BeyondBirdTreeSlotEditor: container has no size');
  }
  let x = ((clientX - rect.left) / rect.width) * BEYOND_BIRD_MEMORY_LOGICAL_WIDTH;
  let y = ((clientY - rect.top) / rect.height) * BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT;
  x = Math.max(0, Math.min(BEYOND_BIRD_MEMORY_LOGICAL_WIDTH, x));
  y = Math.max(0, Math.min(BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT, y));
  return [x, y];
}

/**
 * @param {object} props
 * @param {React.RefObject<HTMLElement>} props.containerRef
 * @param {Array<{ id: number, center: [number, number], hitRadius: number }>} props.slots
 * @param {(index: number, center: [number, number], hitRadius: number) => void} props.onSlotChange
 */
export default function BeyondBirdTreeSlotEditor({ containerRef, slots, onSlotChange }) {
  const [draggingCenterIndex, setDraggingCenterIndex] = useState(null);
  const [resizingIndex, setResizingIndex] = useState(null);

  useEffect(() => {
    if (draggingCenterIndex == null && resizingIndex == null) {
      return undefined;
    }
    const onMove = (e) => {
      const el = containerRef?.current;
      if (!el) return;
      const [lx, ly] = clientToLogical(el, e.clientX, e.clientY);
      if (draggingCenterIndex != null) {
        onSlotChange(draggingCenterIndex, [Math.round(lx), Math.round(ly)], slots[draggingCenterIndex].hitRadius);
      } else if (resizingIndex != null) {
        const slot = slots[resizingIndex];
        const [cx, cy] = slot.center;
        const dx = lx - cx;
        const dy = ly - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const r = Math.max(MIN_HIT_RADIUS, Math.min(MAX_HIT_RADIUS, Math.round(dist)));
        onSlotChange(resizingIndex, [cx, cy], r);
      }
    };
    const onUp = () => {
      setDraggingCenterIndex(null);
      setResizingIndex(null);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [draggingCenterIndex, resizingIndex, containerRef, slots, onSlotChange]);

  const beginCenterDrag = useCallback((e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.currentTarget.setPointerCapture === 'function' && e.pointerId != null) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    setResizingIndex(null);
    setDraggingCenterIndex(index);
    const el = containerRef?.current;
    if (!el) return;
    const [lx, ly] = clientToLogical(el, e.clientX, e.clientY);
    onSlotChange(index, [Math.round(lx), Math.round(ly)], slots[index].hitRadius);
  }, [containerRef, slots, onSlotChange]);

  const beginResize = useCallback((e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.currentTarget.setPointerCapture === 'function' && e.pointerId != null) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    setDraggingCenterIndex(null);
    setResizingIndex(index);
  }, []);

  return (
    <div className="beyond-bird-tree-slot-editor" aria-hidden={false}>
      {slots.map((slot) => {
        const [cx, cy] = slot.center;
        const r = slot.hitRadius;
        const leftPct = (cx / BEYOND_BIRD_MEMORY_LOGICAL_WIDTH) * 100;
        const topPct = (cy / BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT) * 100;
        const sizeWPct = ((r * 2) / BEYOND_BIRD_MEMORY_LOGICAL_WIDTH) * 100;
        const sizeHPct = ((r * 2) / BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT) * 100;
        const handleLeftPct = ((cx + r) / BEYOND_BIRD_MEMORY_LOGICAL_WIDTH) * 100;
        const handleTopPct = (cy / BEYOND_BIRD_MEMORY_LOGICAL_HEIGHT) * 100;
        return (
          <React.Fragment key={slot.id}>
            <div
              className="beyond-bird-tree-slot-editor-hole"
              style={{
                left: `${leftPct}%`,
                top: `${topPct}%`,
                width: `${sizeWPct}%`,
                height: `${sizeHPct}%`,
              }}
            />
            <button
              type="button"
              className="beyond-bird-tree-slot-editor-center"
              style={{ left: `${leftPct}%`, top: `${topPct}%` }}
              aria-label={`Tree hole ${slot.id + 1} center`}
              onPointerDown={(e) => beginCenterDrag(e, slot.id)}
            >
              {slot.id + 1}
            </button>
            <button
              type="button"
              className="beyond-bird-tree-slot-editor-resize"
              style={{ left: `${handleLeftPct}%`, top: `${handleTopPct}%` }}
              aria-label={`Tree hole ${slot.id + 1} resize`}
              onPointerDown={(e) => beginResize(e, slot.id)}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}
